/**
 * useRecorder.ts — Recorder + upload-dialog behavior shared by both practice screens
 * (`/practice` via useLooseRecording, `/legacy` via usePracticeSession).
 *
 * Owns: RecorderService creation and its base callbacks (baseline sampling, error dialog, VAD voice
 * events, sound level), the start/stop primitives, and the discard/save/share handlers for the
 * post-recording upload dialog.
 *
 * Each screen supplies only what genuinely differs, through explicit deps:
 *   - `vadSettings`            — VAD sensitivity/patience (constants vs. the student's sliders)
 *   - `keepRecording`          — whether a finished recording is offered for upload (legacy `recordMe`)
 *   - `ignoreVoiceEvent`       — drop VAD events entirely (legacy TEACHER_ONLY / responsive sentences)
 *   - `onRecordingStarting/Started`, `onVoiceStart/Stop` — screen-specific reactions to recorder events
 *   - `options`                — extra RecorderService options (legacy follower + silence tracking)
 *
 * The state refs are supplied by the caller (see createRecorderState) because /legacy keeps them in
 * its view-wide reactive state, which its overlay reads directly.
 */
import { onBeforeUnmount, type Ref, ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import type { FocusPoint, SegmentPoint, usePracticeUpload, VoiceEvent } from '@/composables/usePracticeUpload'
import store from '@/store'
import { RecorderService, recordingErrorDialog } from '@/util/RecorderService'
import type { RecorderServiceOptions, RecordingType } from '@/util/Recording'

export interface RecorderState {
  backgroundSampling: Ref<boolean>
  backgroundSamplingPercent: Ref<number>
  baseLevel: Ref<number | null>
  focusPoints: Ref<FocusPoint[]>
  pendingRecording: Ref<RecordingType | null>
  pendingRecordingBlobUrl: Ref<string | null>
  recorderService: Ref<RecorderService | null>
  recordingStartedAt: Ref<number>
  segmentPoints: Ref<SegmentPoint[]>
  soundLevel: Ref<number>
  uploadRecordingOverlay: Ref<boolean>
  uploading: Ref<boolean>
  voiceEvents: Ref<VoiceEvent[]>
}

export interface VadSettings {
  sensitivity: number
  silencePatience: number
}

export interface RecorderDeps {
  /** Screen-specific: drop VAD events entirely (no event recorded, no hooks called). */
  ignoreVoiceEvent?: (caller: string) => boolean
  /** Screen-specific: false discards a finished recording without offering the upload dialog. Default: true. */
  keepRecording?: () => boolean
  /** Screen-specific: called after recording state is reset, before the initial focus point is pushed. */
  onRecordingStarting?: () => void
  /** Screen-specific: called after the initial focus point is pushed. */
  onRecordingStarted?: () => void
  onVoiceStart?: () => void
  onVoiceStop?: () => void
  /** Extra RecorderService options; the shared callbacks below take precedence, and `onSoundUpdate` is chained. */
  options?: Partial<RecorderServiceOptions>
  practiceUpload: Pick<ReturnType<typeof usePracticeUpload>, 'pushFocusPoint' | 'uploadPractice'>
  state: RecorderState
  vadSettings: () => VadSettings
}

/** Fresh recorder state, for screens that don't already keep it in a shared reactive object. */
export function createRecorderState(): RecorderState {
  return {
    backgroundSampling: ref(false),
    backgroundSamplingPercent: ref(0),
    baseLevel: ref(null),
    focusPoints: ref([]),
    pendingRecording: ref(null),
    pendingRecordingBlobUrl: ref(null),
    recorderService: ref(null),
    recordingStartedAt: ref(0),
    segmentPoints: ref([]),
    soundLevel: ref(0),
    uploadRecordingOverlay: ref(false),
    uploading: ref(false),
    voiceEvents: ref([]),
  }
}

export function useRecorder(logger: VueLogger, deps: RecorderDeps) {
  const { state } = deps
  // Resolves the deferred onRecordingReady promise. RecorderService revokes the blobUrl once it
  // resolves, so it stays pending until the student picks discard/save/share in the dialog.
  let pendingRecordingResolve: (() => void) | null = null

  const msSinceRecordingStarted = () => Date.now() - state.recordingStartedAt.value

  function resetBackgroundSampling() {
    state.backgroundSampling.value = false
    state.backgroundSamplingPercent.value = 0
  }

  // {{{ RecorderService
  function ensureRecorderService(): RecorderService {
    if (!state.recorderService.value) {
      state.recorderService.value = createRecorderService()
    }
    return state.recorderService.value
  }

  function createRecorderService(): RecorderService {
    const baseOptions = deps.options ?? {}
    return new RecorderService({
      ...baseOptions,
      logger,
      onBaseLevel: (bl: number, perc: number) => {
        state.baseLevel.value = bl
        state.backgroundSamplingPercent.value = perc
      },

      onError: (error: Error) => {
        store.dispatch.dialog.show(recordingErrorDialog(error))
      },

      onRecordingReady: (e: CustomEvent): Promise<void> => {
        return new Promise<void>((resolve) => {
          if (deps.keepRecording && !deps.keepRecording()) {
            return resolve()
          }
          const recording = e.detail.recording as RecordingType
          state.pendingRecording.value = recording
          state.pendingRecordingBlobUrl.value = recording.blobUrl
          state.uploadRecordingOverlay.value = true
          pendingRecordingResolve = resolve
        })
      },

      onSoundUpdate: (n: number) => {
        baseOptions.onSoundUpdate?.(n)
        state.soundLevel.value = n
      },

      onStart: () => {
        logger.debug(`recorder.onStart, baseLevel = ${state.baseLevel.value}`)
        if (!state.baseLevel.value) {
          state.backgroundSampling.value = true
          state.backgroundSamplingPercent.value = 0
        }
      },

      onStartRecording: () => {
        logger.debug(`recorder.onStartRecording, baseLevel = ${state.baseLevel.value}`)
        const service = state.recorderService.value
        if (!service) {
          logger.error('unexpected null recorderService in onStartRecording')
          return
        }
        resetBackgroundSampling()
        const { sensitivity, silencePatience } = deps.vadSettings()
        service.applyVadSettings(sensitivity, silencePatience)
        state.recordingStartedAt.value = Date.now()

        state.voiceEvents.value = []
        state.focusPoints.value = []
        state.segmentPoints.value = []
        deps.onRecordingStarting?.()
        deps.practiceUpload.pushFocusPoint()
        deps.onRecordingStarted?.()
      },

      onVoiceStart: () => {
        if (deps.ignoreVoiceEvent?.('onVoiceStart')) {
          return
        }
        state.voiceEvents.value.push({ voiceStartAt: msSinceRecordingStarted(), voiceStopAt: 0 })
        deps.onVoiceStart?.()
      },

      onVoiceStop: (): Promise<void> => {
        if (!deps.ignoreVoiceEvent?.('onVoiceStop')) {
          state.voiceEvents.value.push({ voiceStartAt: 0, voiceStopAt: msSinceRecordingStarted() })
          deps.onVoiceStop?.()
        }
        return Promise.resolve()
      },

      voiceActivityDetection: true,
    })
  }

  /** Starts capturing; the baseline is only re-sampled when the previous sampling was cancelled. */
  function startRecorder() {
    ensureRecorderService().startRecording(state.baseLevel.value ?? 0)
  }

  /** Stops capturing, or cancels baseline sampling (which invalidates the baseline). */
  function stopRecorder() {
    if (state.backgroundSampling.value) {
      state.baseLevel.value = null
    }
    state.recorderService.value?.stopRecording()
    resetBackgroundSampling()
  }
  // }}}

  // {{{ upload dialog handlers
  function resolvePendingRecording() {
    pendingRecordingResolve?.()
    state.pendingRecording.value = null
    state.pendingRecordingBlobUrl.value = null
    pendingRecordingResolve = null
  }

  function discardRecording() {
    state.uploadRecordingOverlay.value = false
    resolvePendingRecording()
  }

  function uploadPendingRecording(shared: boolean) {
    if (state.pendingRecording.value) {
      deps.practiceUpload.uploadPractice(state.pendingRecording.value, shared)
    }
    // uploadPractice closes the dialog when its upload settles, but bails out early (leaving
    // the dialog open) if there is no focus sentence; close it here unless an upload is running.
    if (state.uploadRecordingOverlay.value && !state.uploading.value) {
      state.uploadRecordingOverlay.value = false
    }
    resolvePendingRecording()
  }

  const saveRecording = () => uploadPendingRecording(false)
  const shareRecording = () => uploadPendingRecording(true)
  // }}}

  onBeforeUnmount(() => {
    state.recorderService.value?.stopRecording()
    state.recorderService.value = null
    resolvePendingRecording()
  })

  return {
    discardRecording,
    ensureRecorderService,
    saveRecording,
    shareRecording,
    startRecorder,
    stopRecorder,
  }
}
